/**
 * WhatsNewRoadmapTabs — shared header showing What's New ⇄ Roadmap ⇄ Testimonials.
 *
 * Visual treatment matches the operated-service pattern:
 *   - Gray pill container with three pill tabs inside.
 *   - Active tab = solid brand purple (`#5B2E83`) with white label.
 *   - Each tab carries a small hover tooltip with an arrow pointing up
 *     to its parent — descriptive copy that hints at what the tab does.
 *     • What's New  → solid navy tooltip "See what we've shipped →"
 *     • Roadmap     → purple→magenta gradient tooltip "See the future · follow along →"
 *     • Testimonials→ solid green tooltip "Hear from customers →"
 */
import { Link, useLocation } from "react-router-dom";
import { Activity, Map, MessageCircle } from "lucide-react";

import { useLanguage } from "@/lib/useLanguage";

export default function WhatsNewRoadmapTabs() {
  const { lang } = useLanguage();
  const { pathname } = useLocation();
  const isRoadmap = pathname.includes("/roadmap");
  const isWhatsNew = pathname.includes("/whats-new");
  const isTestimonials = pathname.includes("/testimonials");

  const prefix = pathname.startsWith("/ja") ? "/ja" : "/en";

  const t =
    lang === "ja"
      ? {
          whatsNew: "最新情報",
          roadmap: "ロードマップ",
          testimonials: "お客様の声",
          sub: "公開された最新の更新、これから先のロードマップ、お客様の声をいつでも切り替えできます。",
          tipWhatsNew: "公開された最新の更新 →",
          tipRoadmap: "これから先の取り組み →",
          tipTestimonials: "お客様の声を見る →",
        }
      : {
          whatsNew: "What's New",
          roadmap: "Roadmap",
          testimonials: "Testimonials",
          sub: "Switch between recent approved updates, the public roadmap, and customer reviews.",
          tipWhatsNew: "See what we've shipped →",
          tipRoadmap: "See the future · follow along →",
          tipTestimonials: "Hear from customers →",
        };

  return (
    <div
      className="bg-white border-b border-slate-200"
      data-testid="whats-new-roadmap-tabs"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="relative flex items-center gap-1 sm:gap-1.5 rounded-full bg-slate-100 p-1 w-full sm:w-auto sm:self-start">
          <Tab
            to={`${prefix}/whats-new`}
            testid="tab-whats-new"
            label={t.whatsNew}
            Icon={Activity}
            active={isWhatsNew}
            tip={t.tipWhatsNew}
            accent="purple"
          />
          <Tab
            to={`${prefix}/roadmap`}
            testid="tab-roadmap"
            label={t.roadmap}
            Icon={Map}
            active={isRoadmap}
            tip={t.tipRoadmap}
            accent="blue"
          />
          <Tab
            to={`${prefix}/testimonials`}
            testid="tab-testimonials"
            label={t.testimonials}
            Icon={MessageCircle}
            active={isTestimonials}
            tip={t.tipTestimonials}
            accent="green"
          />
        </div>
        <p className="text-xs text-slate-500 italic hidden sm:block">{t.sub}</p>
      </div>
    </div>
  );
}

const ACCENTS = {
  purple: {
    bg: "#663399",        // owner-locked logo purple (RebeccaPurple)
    bgHover: "#552B82",
    softBg: "rgba(102,51,153,0.10)",
    text: "#663399",
    ring: "#C4B5FD",
  },
  blue: {
    bg: "#2563EB",
    bgHover: "#1D4ED8",
    softBg: "rgba(37,99,235,0.10)",
    text: "#2563EB",
    ring: "#BFDBFE",
  },
  green: {
    bg: "#16A34A",
    bgHover: "#15803D",
    softBg: "rgba(22,163,74,0.10)",
    text: "#16A34A",
    ring: "#BBF7D0",
  },
};

function Tab({ to, testid, label, Icon, active, tip, accent = "purple" }) {
  const a = ACCENTS[accent] || ACCENTS.purple;
  const base =
    "group/tab flex w-full sm:w-auto sm:inline-flex justify-center items-center gap-1.5 px-3 sm:px-4 py-2 text-[13px] sm:text-[14px] font-semibold rounded-full transition-all whitespace-nowrap";

  return (
    <span className="group relative flex-1 sm:flex-initial">
      <Link
        to={to}
        data-testid={testid}
        className={base}
        style={
          active
            ? {
                backgroundColor: a.bg,
                color: "#ffffff",
                boxShadow: `0 4px 14px -4px ${a.bg}80`,
              }
            : {
                color: "#475569",
                backgroundColor: "transparent",
              }
        }
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = a.softBg;
            e.currentTarget.style.color = a.text;
          } else {
            e.currentTarget.style.backgroundColor = a.bgHover;
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#475569";
          } else {
            e.currentTarget.style.backgroundColor = a.bg;
          }
        }}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </Link>
      {/* Hover tooltip — pill with up-arrow under the tab */}
      <span
        aria-hidden
        data-testid={`${testid}-tip`}
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-3 z-20 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[11.5px] font-semibold tracking-tight shadow-lg opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200 text-white"
        style={{ backgroundColor: a.bg }}
      >
        <span
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 -top-1 w-2.5 h-2.5 rotate-45"
          style={{ backgroundColor: a.bg }}
        />
        {tip}
      </span>
    </span>
  );
}
